import { Component, type ErrorInfo, type ReactNode } from 'react';

import Button from '@/common-components/Button';
import { recordHandledError } from '@/utils/logger.util';

// The one deliberate exception to the "no state in UI files" rule (root CLAUDE.md §4):
// React exposes render-error catching only through a class component's lifecycle, so no hook
// can do this job. Everything else here stays presentational.

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Distinguishes the shell-level boundary from the per-page one in the report. */
  scope: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    recordHandledError(error, {
      scope: this.props.scope,
      componentStack: info.componentStack ?? 'unavailable',
    });
  }

  private readonly handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-[var(--color-divider)] bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-[var(--color-ink)]">Something went wrong</h2>
        <p className="max-w-md text-sm text-[var(--color-ink-muted)]">
          This section failed to render. The error has been reported. Try again, and reload the
          page if it keeps happening.
        </p>
        <Button variant="secondary" onClick={this.handleRetry} className="mt-2">
          Try again
        </Button>
      </div>
    );
  }
}

export default ErrorBoundary;
